import React, { useState, useEffect, useRef } from "react";
import { BellIcon, TrashIcon } from "@heroicons/react/24/outline";
import { formatFuzzyTime } from "@/utils/formatFuzzyTime";
import { useNotifications, useDeleteNotification } from "@/graphql/notifications";
import { useAuth } from "../hooks/useAuth";
import { useNotificationCable } from "@/hooks/useNotificationCable";

interface Notification {
  id: string;
  message: string;
  readAt: string | null;
  createdAt: string;
}

export default function NotificationBell() {
  const { notifications, unreadCount, markAllAsRead, markAsRead, refetch } = useNotifications();
  const { deleteNotification, loading } = useDeleteNotification();
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Subscribe to ActionCable for real-time notifications
  useNotificationCable({
    userId: user?.id,
    onNotification: () => {
      refetch(); // Re-fetch notifications on incoming event
    },
  });

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    }
    if (open) {
      document.addEventListener("mousedown", handleClickOutside);
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
    }
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [open]);

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        type="button"
        className="relative rounded-full bg-white dark:bg-gray-900 p-1 text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
        aria-label="View notifications"
        onClick={() => setOpen((v) => !v)}
      >
        <BellIcon className="h-6 w-6" aria-hidden="true" />
        {unreadCount > 0 && (
          <span className="absolute -top-0.5 -right-0.5 inline-flex items-center justify-center px-1.5 py-0.5 text-xs font-bold leading-none text-white bg-indigo-600 rounded-full">
            {unreadCount}
          </span>
        )}
      </button>
      {open && (
        <div className="absolute right-0 z-30 mt-2 w-80 origin-top-right rounded-md bg-white dark:bg-gray-800 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
          <div className="p-4 border-b border-gray-100 dark:border-gray-700 flex items-center justify-between">
            <span className="font-semibold text-gray-900 dark:text-gray-100">Notifications</span>
            <button
              onClick={markAllAsRead}
              className="text-xs text-indigo-600 hover:underline disabled:text-gray-400"
              disabled={unreadCount === 0}
            >
              Mark all as read
            </button>
          </div>
          <ul className="max-h-80 overflow-y-auto divide-y divide-gray-100 dark:divide-gray-700">
            {notifications.length === 0 ? (
              <li className="p-4 text-gray-500 text-center">No notifications</li>
            ) : (
              notifications.map((n: Notification) => (
                <li
                  key={n.id}
                  className={`p-4 flex items-start gap-2 cursor-pointer ${!n.readAt ? "bg-indigo-50 dark:bg-indigo-900/30" : ""}`}
                >
                  <div className="flex-1" onClick={() => markAsRead(n.id)}>
                    <div className="text-sm text-gray-900 dark:text-gray-100">{n.message}</div>
                    <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">{formatFuzzyTime(n.createdAt)}</div>
                    {!n.readAt && (
                      <span className="inline-block mt-2 px-2 py-0.5 text-xs bg-indigo-100 dark:bg-indigo-700 text-indigo-700 dark:text-indigo-100 rounded">
                        Unread
                      </span>
                    )}
                  </div>
                  <button
                    type="button"
                    aria-label="Delete notification"
                    className="ml-2 p-1 rounded hover:bg-red-100 dark:hover:bg-red-900/30 text-red-600 disabled:opacity-50"
                    onClick={async (e) => {
                      e.stopPropagation();
                      setDeletingId(n.id);
                      try {
                        await deleteNotification(n.id);
                        await refetch();
                      } finally {
                        setDeletingId(null);
                      }
                    }}
                    disabled={loading && deletingId === n.id}
                  >
                    {loading && deletingId === n.id ? (
                      <svg className="animate-spin h-5 w-5 text-red-600" viewBox="0 0 24 24" fill="none"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" /></svg>
                    ) : (
                      <TrashIcon className="h-5 w-5" />
                    )}
                  </button>
                </li>
              ))
            )}
          </ul>
        </div>
      )}
    </div>
  );
}
